import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi, acquirePortalToken } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import { useProject } from '../context/ProjectContext';
import { parseAiOutput, type ParseResult, type TcFormat } from '../utils/aiOutputParser';
import { extractDocContent } from '../utils/documentExtractor';
import { Icon } from '../components/icons';
import Button from '../components/Button';
import AiThinkingIndicator from '../components/AiThinkingIndicator';
import EmptyState from '../components/EmptyState';
import GeneratorHeroIllustration from '../components/illustrations/GeneratorHeroIllustration';
import { FINT_EXTENSION_ID } from '../constants/extension';
import type { TestCase, SimilarCatalogItem } from '../types';

// ─── Prompt templates (mirror src/prompts.js) ────────────────────────────────

function buildJiraPrompt(title: string, desc: string, acceptance: string, other: string, additionalInfo: string, types: string[]): string {
  const stepsBlock = [
    title       ? `JIRA Title: ${title}`           : '',
    desc        ? `Description: ${desc}`            : '',
    acceptance  ? `Acceptance Criteria: ${acceptance}` : '',
    other       ? `Other Details: ${other}`         : '',
    types.length ? `Testcase Types: ${types.join(', ')}` : '',
    additionalInfo ? `\nAdditional Information for AI: ${additionalInfo}` : '',
  ].filter(Boolean).join('\n');

  return `Context: You are assisting in software manual testing. Create comprehensive test cases covering all relevant scenarios.

Rules:
- Ensure the output STRICTLY adheres to the format below.
- Each test case MUST contain explicit step-by-step actions.
- Generate at least 10 Positive, 15 Negative, and 10 Edge test cases.
- No extra text or explanations. Avoid ###.

Input:
${stepsBlock}

Desired Output:
Test Case ID: TC_POS_01
Title: [specific title]
Test Type: Positive
Preconditions: [preconditions]
Test Steps:
  Step 1. [action]
  Step 2. [action]
Test Data: [data]
Expected Result: [result]

Repeat for every test case.`;
}

function buildDocumentPrompt(documentText: string, additionalInfo: string, types: string[], format: TcFormat): string {
  if (format === 'gherkin') {
    return `Context: You are a senior QA engineer specialising in BDD. Generate comprehensive Gherkin test scenarios based on this document.
Rules: Every Scenario must map to a specific requirement. Use actual field names/values. Each Scenario has a unique ID starting with TC_BDD_. Generate at least 10 Scenarios per type. STRICTLY follow the output format. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Document:\n${documentText}\nAdditional Information:\n${additionalInfo || 'None'}\nRequired Testcase Types: ${types.join(', ')}

Output Format (FOLLOW EXACTLY):
Feature: [Feature name]

Scenario: [TC_BDD_001] [Positive] [Descriptive title]
  Given [precondition]
  When [action]
  And [action]
  Then [expected result]

Repeat Scenario block for every test case. Do NOT repeat the Feature line.`;
  }
  if (format === 'tdd') {
    return `Context: You are a senior QA engineer specialising in data-driven testing. Generate TDD test cases from this document.
Rules: Every test case must reference an exact requirement. Use actual field names/values. Generate at least 10 per type. STRICTLY follow the format below. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Document:\n${documentText}\nAdditional Information:\n${additionalInfo || 'None'}\nRequired Testcase Types: ${types.join(', ')}

Output Format (FOLLOW EXACTLY — blank line between blocks):
Test ID: TC_TDD_001
Test Name: [name]
Requirement: [FR-number and name]
Type: [Positive/Negative/Edge]
Input Data: [field:value pairs]
Steps Summary: [1. step. 2. step.]
Expected Output: [output]
Pass Criteria: [criteria]

Repeat this block for every test case.`;
  }
  return `Context: You are a senior QA engineer. Generate comprehensive manual test cases from this requirement document.
Rules: Every test case must reference a specific requirement. Use actual field names/values. Generate at least 10 per type. Follow the output format exactly. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Document:\n${documentText}\nAdditional Information:\n${additionalInfo || 'None'}\nRequired Testcase Types: ${types.join(', ')}

Output Format:
Test Case ID: TC_001
Title: [title]
Requirement: [requirement]
Test Type: [Positive/Negative/Edge]
Preconditions: [preconditions]
Test Steps:
  Step 1. [action]
Test Data: [data]
Expected Result: [result]

Repeat for every test case.`;
}

function buildImagePrompt(additionalInfo: string, types: string[], format: TcFormat): string {
  const typesStr = types.join(', ');
  if (format === 'gherkin') {
    return `You are a senior QA engineer specialising in BDD. Generate comprehensive Gherkin test scenarios based on this image.
Rules: Every Scenario must map to a specific element visible in the image. Use actual field names/labels. Each has a unique ID starting with TC_BDD_. Generate at least 10 per type. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Additional Information: ${additionalInfo || 'None'}\nRequired Testcase Types: ${typesStr}

Output Format (FOLLOW EXACTLY):
Feature: [Feature name]

Scenario: [TC_BDD_001] [Positive] [Descriptive title]
  Given [precondition]
  When [action]
  And [action]
  Then [expected result]

Repeat Scenario block for every test case. Do NOT repeat the Feature line.`;
  }
  if (format === 'tdd') {
    return `You are a senior QA engineer. Generate TDD test cases based on this image.
Rules: Every test case must reference an element visible in the image. Use actual field names. Generate at least 10 per type. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Additional Information: ${additionalInfo || 'None'}\nRequired Testcase Types: ${typesStr}

Output Format:
Test ID: TC_TDD_001
Test Name: [name]
Requirement: [screen/element]
Type: [Positive/Negative/Edge]
Input Data: [field:value]
Steps Summary: [1. step. 2. step.]
Expected Output: [output]
Pass Criteria: [criteria]

Repeat for every test case.`;
  }
  return `You are a senior QA engineer. Generate comprehensive manual test cases based on this image.
Rules: Every test case must reference a specific element visible in the image. Use actual field names/labels. Generate at least 10 per type. Plain text only — no Markdown formatting (no **, ##, or other decoration).
Additional Information: ${additionalInfo || 'None'}\nRequired Testcase Types: ${typesStr}

Output Format:
Test Case ID: TC_001
Title: [title]
Requirement: [screen/element from image]
Test Type: [Positive/Negative/Edge]
Preconditions: [preconditions]
Test Steps:
  Step 1. [action]
Test Data: [data]
Expected Result: [result]

Repeat for every test case.`;
}

// ─── Parsed result → TestCase[] ──────────────────────────────────────────────

function deduplicateIds(cases: TestCase[]): TestCase[] {
  const seen = new Map<string, number>();
  return cases.map(c => {
    if (!seen.has(c.id)) { seen.set(c.id, 1); return c; }
    const n = seen.get(c.id)! + 1;
    seen.set(c.id, n);
    return { ...c, id: `${c.id}_${n}` };
  });
}

function parsedToTestCases(parsed: ParseResult): TestCase[] {
  let cases: TestCase[] = [];
  if (parsed.format === 'normal' && parsed.normalCases) {
    cases = parsed.normalCases.map(c => ({
      id: c.id, title: c.title, type: c.type,
      preconditions: c.preconditions, steps: c.steps,
      testData: c.testData, expectedResult: c.expectedResult,
      author: '', createdAt: '', status: 'pending', approvals: [], rejections: [],
    }));
  } else if (parsed.format === 'gherkin' && parsed.gherkinCases) {
    cases = parsed.gherkinCases.map(c => ({
      id: c.id, title: c.scenarioName, type: c.type,
      preconditions: '', steps: c.stepLines,
      testData: '', expectedResult: '',
      author: '', createdAt: '', status: 'pending', approvals: [], rejections: [],
    }));
  } else if (parsed.format === 'tdd' && parsed.tddCases) {
    cases = parsed.tddCases.map(c => ({
      id: c.id, title: c.name, type: c.type,
      preconditions: c.requirement, steps: c.stepsSummary,
      testData: c.inputData, expectedResult: c.expectedOutput,
      author: '', createdAt: '', status: 'pending', approvals: [], rejections: [],
    }));
  }
  return deduplicateIds(cases);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type Mode = 'jira' | 'manual' | 'document';

interface JiraFields {
  title: string; description: string; acceptance: string; other: string; additionalInfo: string;
}

interface TcTypeState { positive: boolean; negative: boolean; edge: boolean; }

function selectedTypes(s: TcTypeState): string[] {
  return [s.positive && 'Positive', s.negative && 'Negative', s.edge && 'Edge'].filter(Boolean) as string[];
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function GeneratorPage() {
  const { instance } = useMsal();
  const cfg          = useRuntimeConfig();
  const navigate     = useNavigate();
  const api          = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);
  const { activeProjectId, activeProject } = useProject();

  const [mode, setMode]           = useState<Mode>('jira');
  const [format, setFormat]       = useState<TcFormat>('normal');
  const [tcTypes, setTcTypes]     = useState<TcTypeState>({ positive: true, negative: false, edge: false });

  // JIRA mode state
  const [jiraId, setJiraId]       = useState('');
  const [jiraFields, setJiraFields] = useState<JiraFields>({ title: '', description: '', acceptance: '', other: '', additionalInfo: '' });
  const [jiraFetching, setJiraFetching] = useState(false);
  const [jiraFetchError, setJiraFetchError] = useState('');
  const [jiraFetched, setJiraFetched]   = useState(false);

  // Manual mode state
  const [manualTitle, setManualTitle]       = useState('');
  const [manualDesc, setManualDesc]         = useState('');
  const [manualAcceptance, setManualAcceptance] = useState('');
  const [manualAdditional, setManualAdditional] = useState('');

  // Document mode state
  const [docFile, setDocFile]     = useState<File | null>(null);
  const [docAdditional, setDocAdditional] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [genStatus, setGenStatus]  = useState('');
  const [genError, setGenError]    = useState('');
  const [parsedResult, setParsedResult] = useState<ParseResult | null>(null);
  const [allCases, setAllCases]    = useState<TestCase[]>([]);
  const [selected, setSelected]    = useState<Set<string>>(new Set());
  // Which confirmed context version (if any) was injected for the most recent generation —
  // carried into submitToPortal() so the session can be traced back to it.
  const [genContextVersionId, setGenContextVersionId] = useState<string | null>(null);

  // Dedup state
  const [forceGenerate, setForceGenerate]   = useState(false);
  const [similarItems, setSimilarItems]     = useState<SimilarCatalogItem[]>([]);
  const [showSimilar, setShowSimilar]       = useState(true);

  // Submit state
  const [sessionTitle, setSessionTitle] = useState('');
  const [sessionTagsInput, setSessionTagsInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Recorder modal state
  const [recorderModal, setRecorderModal] = useState<{ url: string; launching: boolean; error: string } | null>(null);

  // Page (5 per page)
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(allCases.length / PAGE_SIZE);
  const pageCases  = allCases.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [allCases]);

  // ── Fetch JIRA ──────────────────────────────────────────────────────────────
  async function fetchJira() {
    if (!jiraId.trim()) return;
    setJiraFetching(true); setJiraFetchError(''); setJiraFetched(false);
    try {
      const data = await api.fetchJiraIssue(jiraId.trim()) as any;
      const key  = data.key ?? '';
      const fields = data.fields ?? {};
      const summary = fields.summary ?? '';
      const desc    = (fields.description ?? '') as string;

      let mainDesc = desc;
      const acIdx  = desc.search(/acceptance criteria/i);
      const otIdx  = desc.search(/other information/i);
      const cutIdx = acIdx !== -1 && otIdx !== -1 ? Math.min(acIdx, otIdx) : acIdx !== -1 ? acIdx : otIdx;
      if (cutIdx !== -1) mainDesc = desc.substring(0, cutIdx).trim().replace(/h2\..*/i, '').trim();

      let acceptance = 'N/A';
      const acMatch = desc.match(/acceptance criteria\s*([\s\S]*?)(?:\n\s*other information|$)/i);
      if (acMatch?.[1]) acceptance = acMatch[1].replace(/h2\..*/i, '').replace(/\n{2,}/g, '\n').replace(/\n?N\/A\s*$/i, '').trim();

      let other = 'N/A';
      const otMatch = desc.match(/other information\s*([\s\S]*?)(?:$|\n)/i);
      if (otMatch?.[1]) other = otMatch[1].trim();

      setJiraFields({ title: `${key} - ${summary}`, description: mainDesc, acceptance, other, additionalInfo: '' });
      setJiraFetched(true);
    } catch (e: any) {
      setJiraFetchError(e.message || 'Failed to fetch JIRA issue');
    } finally {
      setJiraFetching(false);
    }
  }

  // ── Generate ────────────────────────────────────────────────────────────────
  async function generate() {
    const types = selectedTypes(tcTypes);
    if (!types.length) { setGenError('Select at least one test case type.'); return; }
    setGenerating(true); setGenError(''); setParsedResult(null); setAllCases([]); setSelected(new Set());
    setSimilarItems([]); setShowSimilar(true);

    try {
      // ── Dedup check against ApprovedCatalog ─────────────────────────────────
      let covered: SimilarCatalogItem[] = [];
      if (!forceGenerate) {
        const queryText = (
          mode === 'jira'     ? jiraFields.title :
          mode === 'manual'   ? manualTitle :
          /* document */        (docAdditional.trim() || docFile?.name.replace(/\.[^/.]+$/, '') || '')
        ).trim();

        if (queryText.length > 10) {
          setGenStatus('Checking catalog for similar test cases…');
          const similar = await api.getSimilar(queryText, 20);
          covered = similar.filter(s => s.score >= 0.80);
          setSimilarItems(covered);

          if (covered.length >= 3) {
            setGenerating(false);
            setGenStatus('');
            return; // banner is shown, generation blocked — user must tick Force
          }
        }
      }

      // Only inject covered context when there are 1–2 partial matches (not enough to block)
      const coveredBlock = covered.length > 0
        ? '\n\nNOTE: The following test areas are already in the approved catalog — avoid exact duplicates but you may still generate related scenarios:\n' +
          covered.map(s => `- ${s.title} (${Math.round(s.score * 100)}% match)`).join('\n')
        : '';

      let aiText: string;

      if (mode === 'jira' || mode === 'manual') {
        const title = mode === 'jira' ? jiraFields.title : manualTitle;
        const desc  = mode === 'jira' ? jiraFields.description : manualDesc;
        const acc   = mode === 'jira' ? jiraFields.acceptance : manualAcceptance;
        const other = mode === 'jira' ? jiraFields.other : '';
        const addl  = (mode === 'jira' ? jiraFields.additionalInfo : manualAdditional) + coveredBlock;
        setGenStatus('Generating test cases…');
        const result = await api.callAI(
          buildJiraPrompt(title, desc, acc, other, addl, types), types, { projectId: activeProjectId }
        );
        aiText = result.text;
        setGenContextVersionId(result.contextVersionId ?? null);
      } else {
        if (!docFile) { setGenError('Please upload a file first.'); setGenerating(false); return; }
        setGenStatus('Extracting document content…');
        const { text, images } = await extractDocContent(docFile);
        if (!text && images.length === 0) throw new Error('Could not extract any content from this file.');
        const prompt = images.length && !text
          ? buildImagePrompt(docAdditional + coveredBlock, types, format)
          : buildDocumentPrompt(text, docAdditional + coveredBlock, types, format);
        setGenStatus('Generating test cases…');
        const docResult = await api.callAIWithContent(prompt, images, types, { projectId: activeProjectId });
        aiText = docResult.text;
        setGenContextVersionId(docResult.contextVersionId ?? null);
      }

      const parsed = parseAiOutput(aiText, format);
      if (!parsed) { setGenError('Could not parse AI output. Try a different format or check the document.'); return; }
      const cases = parsedToTestCases(parsed);
      setParsedResult(parsed);
      setAllCases(cases);
      setSelected(new Set(cases.map(c => c.id)));
      setSessionTitle(mode === 'jira' ? jiraFields.title : mode === 'manual' ? manualTitle : docFile?.name ?? 'Generated');
    } catch (e: any) {
      setGenError(e.message || 'Generation failed.');
    } finally {
      setGenerating(false); setGenStatus('');
    }
  }

  // ── Submit to portal ────────────────────────────────────────────────────────
  async function submitToPortal() {
    const toSubmit = allCases.filter(c => selected.has(c.id));
    if (!toSubmit.length) { setSubmitError('Select at least one test case.'); return; }
    if (!sessionTitle.trim()) { setSubmitError('Enter a session title.'); return; }
    const tags = sessionTagsInput.trim()
      ? sessionTagsInput.split(',').map(t => t.trim()).filter(Boolean)
      : undefined;
    setSubmitting(true); setSubmitError('');
    try {
      const { sessionId } = await api.createSession({
        title: sessionTitle.trim(), outputType: format, cases: toSubmit, tags,
        projectId: activeProjectId, contextVersionId: genContextVersionId,
      });
      navigate(`/sessions/${sessionId}`);
    } catch (e: any) {
      setSubmitError(e.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────
  function toggleSelect(id: string) {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function toggleAll() {
    setSelected(prev => prev.size === allCases.length ? new Set() : new Set(allCases.map(c => c.id)));
  }

  function togglePage() {
    const allPageSelected = pageCases.every(c => selected.has(c.id));
    setSelected(prev => {
      const n = new Set(prev);
      if (allPageSelected) {
        pageCases.forEach(c => n.delete(c.id));
      } else {
        pageCases.forEach(c => n.add(c.id));
      }
      return n;
    });
  }

  // ── Recorder (Feature 1) ────────────────────────────────────────────────────
  function openRecorderModal() {
    setRecorderModal({ url: '', launching: false, error: '' });
  }

  async function launchRecorder() {
    if (!recorderModal) return;
    const { url } = recorderModal;
    if (!url.trim()) {
      setRecorderModal(m => m ? { ...m, error: 'Please enter the URL of the app you want to record.' } : m);
      return;
    }
    setRecorderModal(m => m ? { ...m, launching: true, error: '' } : m);
    try {
      const authToken = await acquirePortalToken(instance, cfg.msalClientId);
      await sendToExtension(FINT_EXTENSION_ID, { type: 'OPEN_RECORDER', targetUrl: url.trim(), authToken });
      setRecorderModal(null);
    } catch (e: any) {
      setRecorderModal(m => m ? {
        ...m,
        launching: false,
        error: `Could not reach the extension: ${e?.message || 'unknown error'}. Make sure the Fint QualiForge Test Recorder extension is installed and enabled.`,
      } : m);
    }
  }

  function sendToExtension(extId: string, message: object): Promise<void> {
    return new Promise((resolve, reject) => {
      const cr = (window as any).chrome?.runtime;
      if (!cr?.sendMessage) { reject(new Error('chrome.runtime not available')); return; }
      cr.sendMessage(extId, message, (resp: any) => {
        if (cr.lastError) { reject(new Error(cr.lastError.message || 'Chrome runtime error')); return; }
        if (!resp?.success) { reject(new Error('Extension returned no response')); return; }
        resolve();
      });
    });
  }

  const canGenerate = (() => {
    if (mode === 'jira')     return (jiraFetched && jiraFields.title) || (!jiraFetched && jiraFields.title.trim() !== '');
    if (mode === 'manual')   return manualTitle.trim().length > 0 && manualDesc.trim().length > 0;
    if (mode === 'document') return !!docFile;
    return false;
  })();

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="page generator-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Generator</h1>
          <p className="page-subtitle">Generate AI test cases from JIRA stories, manual input, or uploaded documents.</p>
          {activeProject ? (
            <p className="hint-text" style={{ marginTop: 4 }}>
              Grounded in project context: <strong>{activeProject.name}</strong>
            </p>
          ) : (
            <p className="hint-text" style={{ marginTop: 4 }}>
              No project selected — generating in generic mode.{' '}
              <Link to="/settings/projects">Set up a project</Link> to ground output in real product context.
            </p>
          )}
        </div>
      </div>

      {/* ── Mode + Format + Type controls ── */}
      <div className="gen-controls-bar">
        <div className="gen-control-group">
          <span className="gen-control-label">Source</span>
          <div className="gen-toggle-row">
            {(['jira','manual','document'] as Mode[]).map(m => (
              <button key={m} className={`gen-toggle-btn ${mode === m ? 'active' : ''}`} onClick={() => { setMode(m); setGenError(''); setParsedResult(null); setAllCases([]); }}>
                {m === 'jira' ? 'JIRA' : m === 'manual' ? 'Manual' : 'Document'}
              </button>
            ))}
            <button
              className="gen-toggle-btn gen-record-btn"
              onClick={openRecorderModal}
              title="Open the Fint QualiForge Test Recorder extension in a new window to record interactions and generate test cases"
            >
              <Icon name="record" size={12} /> Record
            </button>
          </div>
        </div>
        <div className="gen-control-group">
          <span className="gen-control-label">Format</span>
          <div className="gen-toggle-row">
            {(['normal','gherkin','tdd'] as TcFormat[]).map(f => (
              <button key={f} className={`gen-toggle-btn ${format === f ? 'active' : ''}`} onClick={() => setFormat(f)}>
                {f === 'normal' ? 'Normal' : f === 'gherkin' ? 'Gherkin (BDD)' : 'Excel (TDD)'}
              </button>
            ))}
          </div>
        </div>
        <div className="gen-control-group">
          <span className="gen-control-label">Test Type</span>
          <div className="gen-toggle-row">
            {(['positive','negative','edge'] as const).map(t => (
              <label key={t} className={`gen-check-label ${tcTypes[t] ? 'active' : ''}`}>
                <input type="checkbox" checked={tcTypes[t]} onChange={() => setTcTypes(s => ({ ...s, [t]: !s[t] }))} />
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* ── Input panel ── */}
      <div className="gen-input-panel">
        {mode === 'jira' && (
          <div className="gen-card">
            <h2 className="gen-section-title">JIRA Issue</h2>
            <div className="gen-row">
              <input
                className="form-input gen-jira-input"
                placeholder="e.g. SCRUM-123"
                value={jiraId}
                onChange={e => { setJiraId(e.target.value); setJiraFetched(false); }}
                onKeyDown={e => e.key === 'Enter' && fetchJira()}
              />
              <button className="btn btn-outline btn-sm" onClick={fetchJira} disabled={jiraFetching || !jiraId.trim()}>
                {jiraFetching ? 'Fetching…' : 'Fetch Story'}
              </button>
            </div>
            {jiraFetchError && <div className="gen-error">{jiraFetchError}</div>}
            {jiraFetched && (
              <div className="gen-jira-fields">
                <FieldRow label="Title" value={jiraFields.title} onChange={v => setJiraFields(f => ({ ...f, title: v }))} />
                <FieldRow label="Description" value={jiraFields.description} onChange={v => setJiraFields(f => ({ ...f, description: v }))} multiline />
                <FieldRow label="Acceptance Criteria" value={jiraFields.acceptance} onChange={v => setJiraFields(f => ({ ...f, acceptance: v }))} multiline />
                <FieldRow label="Other Details" value={jiraFields.other} onChange={v => setJiraFields(f => ({ ...f, other: v }))} multiline />
                <FieldRow label="Additional Info for AI" value={jiraFields.additionalInfo} onChange={v => setJiraFields(f => ({ ...f, additionalInfo: v }))} multiline placeholder="Optional: sample steps, focus areas…" />
              </div>
            )}
          </div>
        )}

        {mode === 'manual' && (
          <div className="gen-card">
            <h2 className="gen-section-title">Manual Input</h2>
            <FieldRow label="Title *" value={manualTitle} onChange={setManualTitle} placeholder="Feature or user story title" />
            <FieldRow label="Description *" value={manualDesc} onChange={setManualDesc} multiline placeholder="Describe the feature or user story" />
            <FieldRow label="Acceptance Criteria *" value={manualAcceptance} onChange={setManualAcceptance} multiline placeholder="Acceptance criteria" />
            <FieldRow label="Additional Info for AI" value={manualAdditional} onChange={setManualAdditional} multiline placeholder="Optional: sample steps, context…" />
          </div>
        )}

        {mode === 'document' && (
          <div className="gen-card">
            <h2 className="gen-section-title">Upload Document or Image</h2>
            <label
              className={`gen-dropzone ${docFile ? 'has-file' : ''}`}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
              onDragLeave={e => e.currentTarget.classList.remove('drag-over')}
              onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove('drag-over'); const f = e.dataTransfer.files[0]; if (f) setDocFile(f); }}
            >
              <span className="gen-dropzone-icon"><Icon name="document" size={26} /></span>
              <span className="gen-dropzone-text">{docFile ? docFile.name : 'Click to upload or drag & drop'}</span>
              <span className="gen-dropzone-hint">PDF · Word (.docx) · Excel (.xlsx) · TXT · CSV · PNG · JPG · GIF · WEBP</span>
              <input
                ref={fileInputRef} type="file"
                accept=".pdf,.docx,.xlsx,.xls,.txt,.csv,.md,.png,.jpg,.jpeg,.gif,.webp,.bmp"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files?.[0]) setDocFile(e.target.files[0]); }}
              />
            </label>
            {docFile && (
              <button className="btn btn-outline btn-sm" style={{ marginTop: 8 }} onClick={() => { setDocFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                Remove file
              </button>
            )}
            <FieldRow label="Additional Info for AI" value={docAdditional} onChange={setDocAdditional} multiline placeholder="Optional: focus areas, context…" />
          </div>
        )}

        <div className="gen-actions-row">
          <Button variant="ai" isGenerating={generating} onClick={generate} disabled={generating || !canGenerate}>
            {generating ? <AiThinkingIndicator status={genStatus} fallback="Generating…" /> : <><Icon name="sparkle" size={14} /> Generate Test Cases with AI</>}
          </Button>
          <label className="gen-force-label">
            <input type="checkbox" checked={forceGenerate} onChange={e => setForceGenerate(e.target.checked)} />
            Force full generation (ignore catalog)
          </label>
          {genError && <span className="gen-error">{genError}</span>}
        </div>

        {/* Already-covered notice */}
        {similarItems.length > 0 && showSimilar && (
          <div className="gen-dedup-banner">
            <div className="gen-dedup-header">
              <span>
                <strong>{similarItems.length} similar test case{similarItems.length !== 1 ? 's' : ''} already exist</strong> in your catalog.
                {similarItems.length >= 3
                  ? ' Generation blocked — tick "Force full generation" to proceed anyway.'
                  : ' The AI has been told to avoid duplicating these areas.'}
              </span>
              <button className="gen-dedup-dismiss" onClick={() => setShowSimilar(false)}><Icon name="x" size={13} /></button>
            </div>
            <ul className="gen-dedup-list">
              {similarItems.map(item => (
                <li key={item.catalogId}>
                  <span className="gen-dedup-title">{item.title}</span>
                  <span className="gen-dedup-score">{Math.round(item.score * 100)}% match</span>
                  {item.remoteRef && (
                    <span className="gen-dedup-ref">{item.remoteRef.adapter}: {item.remoteRef.remoteId}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ── Results ── */}
      {allCases.length === 0 && !generating && (
        <EmptyState illustration={<GeneratorHeroIllustration />}>
          Fill in the details above and generate — your test cases will show up here, ready to review and submit.
        </EmptyState>
      )}
      {allCases.length > 0 && (
        <div className="gen-results">
          <div className="gen-results-header">
            <div>
              <h2 className="gen-section-title" style={{ marginBottom: 2 }}>
                Generated Test Cases
                <span className="gen-count-badge">{allCases.length} cases</span>
              </h2>
              {parsedResult?.featureName && (
                <div className="gen-feature-name">Feature: {parsedResult.featureName}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn btn-outline btn-sm" onClick={toggleAll}>
                {allCases.length > 0 && selected.size === allCases.length ? 'Unselect All' : 'Select All'}
              </button>
              <span className="gen-selected-count">{selected.size} of {allCases.length} selected</span>
            </div>
          </div>

          <div className="gen-table-wrapper">
            <table className="gen-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}><input type="checkbox" checked={pageCases.length > 0 && pageCases.every(c => selected.has(c.id))} onChange={togglePage} /></th>
                  {format === 'normal' && <><th>ID</th><th>Title</th><th>Type</th><th>Preconditions</th><th>Steps</th><th>Test Data</th><th>Expected Result</th></>}
                  {format === 'gherkin' && <><th>ID</th><th>Type</th><th>Scenario Name</th><th>Gherkin Steps</th></>}
                  {format === 'tdd' && <><th>ID</th><th>Name</th><th>Requirement</th><th>Type</th><th>Input Data</th><th>Steps Summary</th><th>Expected Output</th><th>Pass Criteria</th></>}
                </tr>
              </thead>
              <tbody>
                {pageCases.map(tc => (
                  <tr key={tc.id} className={selected.has(tc.id) ? 'gen-row-selected' : ''}>
                    <td><input type="checkbox" checked={selected.has(tc.id)} onChange={() => toggleSelect(tc.id)} /></td>
                    {format === 'normal' && <>
                      <td className="gen-id-cell">{tc.id}</td>
                      <td>{tc.title}</td>
                      <td><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>
                      <td className="gen-cell-wrap">{tc.preconditions}</td>
                      <td className="gen-cell-wrap"><ol className="steps-list">{tc.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></td>
                      <td className="gen-cell-wrap">{tc.testData}</td>
                      <td className="gen-cell-wrap">{tc.expectedResult}</td>
                    </>}
                    {format === 'gherkin' && <>
                      <td className="gen-id-cell">{tc.id}</td>
                      <td><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>
                      <td>{tc.title}</td>
                      <td className="gen-cell-wrap gen-gherkin-steps">
                        {tc.steps.map((line, i) => {
                          const kw = line.match(/^(Given|When|And|Then|But)\s+(.*)/i);
                          if (!kw) return <div key={i}>{line}</div>;
                          const colors: Record<string, string> = { Given: 'var(--success)', When: 'var(--accent)', And: 'var(--t2)', Then: 'var(--warn)', But: 'var(--danger)' };
                          const keyword = kw[1].charAt(0).toUpperCase() + kw[1].slice(1).toLowerCase();
                          return <div key={i}><strong style={{ color: colors[keyword] || 'var(--t1)' }}>{keyword}</strong> {kw[2]}</div>;
                        })}
                      </td>
                    </>}
                    {format === 'tdd' && <>
                      <td className="gen-id-cell">{tc.id}</td>
                      <td>{tc.title}</td>
                      <td className="gen-cell-wrap">{tc.preconditions}</td>
                      <td><span className={`type-badge type-${(tc.type || '').toLowerCase()}`}>{tc.type}</span></td>
                      <td className="gen-cell-wrap">{tc.testData}</td>
                      <td className="gen-cell-wrap"><ol className="steps-list">{tc.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></td>
                      <td className="gen-cell-wrap">{tc.expectedResult}</td>
                      <td className="gen-cell-wrap">{/* passCriteria stored in remoteKey slot */}{(tc as any).passCriteria ?? ''}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="gen-pagination">
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
              <span className="gen-page-info">Page {page} of {totalPages}</span>
              <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
            </div>
          )}

          {/* Submit panel */}
          <div className="gen-submit-panel">
            <h3 className="gen-section-title" style={{ marginBottom: 12 }}>Submit to Portal</h3>
            <div className="gen-submit-row">
              <input
                className="form-input"
                placeholder="Session title"
                value={sessionTitle}
                onChange={e => setSessionTitle(e.target.value)}
                style={{ flex: 1 }}
              />
              <input
                className="form-input"
                placeholder="Tags (comma-separated, e.g. Login, Payments)"
                value={sessionTagsInput}
                onChange={e => setSessionTagsInput(e.target.value)}
                style={{ flex: 1 }}
              />
              <button className="btn btn-primary" onClick={submitToPortal} disabled={submitting || selected.size === 0}>
                {submitting ? 'Submitting…' : `Submit ${selected.size} Case${selected.size !== 1 ? 's' : ''} for Review`}
              </button>
            </div>
            {submitError && <div className="gen-error">{submitError}</div>}
          </div>
        </div>
      )}

      {/* ── Recorder modal ── */}
      {recorderModal && (
        <div className="modal-overlay" onClick={() => setRecorderModal(null)}>
          <div className="modal" style={{ width: '100%', maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Open Test Recorder</span>
              <button className="modal-close" onClick={() => setRecorderModal(null)}><Icon name="x" size={15} /></button>
            </div>
            <div className="modal-body">
              <p className="modal-desc">
                A new window will open at the URL you enter, with the Fint QualiForge Test Recorder extension active in the side panel.
                Record your interactions, then use <strong>Generate TC with AI</strong> and <strong>Submit to Portal</strong> in the extension.
              </p>
              <div className="field-wrap">
                <label className="field-label">Target app URL</label>
                <input
                  className="form-input"
                  type="url"
                  placeholder="https://your-app.example.com"
                  value={recorderModal.url}
                  onChange={e => setRecorderModal(m => m ? { ...m, url: e.target.value, error: '' } : m)}
                  onKeyDown={e => { if (e.key === 'Enter') launchRecorder(); }}
                  autoFocus
                  style={{ width: '100%' }}
                />
              </div>
              {recorderModal.error && <p className="gen-error">{recorderModal.error}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setRecorderModal(null)} disabled={recorderModal.launching}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={launchRecorder} disabled={recorderModal.launching}>
                {recorderModal.launching ? 'Opening…' : <><Icon name="record" size={12} /> Start Recording</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shared field row component ───────────────────────────────────────────────
interface FieldRowProps {
  label: string; value: string; onChange: (v: string) => void;
  multiline?: boolean; placeholder?: string;
}
function FieldRow({ label, value, onChange, multiline, placeholder }: FieldRowProps) {
  return (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label>{label}</label>
      {multiline
        ? <textarea className="form-input" rows={3} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ resize: 'vertical' }} />
        : <input className="form-input" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />}
    </div>
  );
}
